// useApprovals — the Unified Approval Command Center's data layer (#175).
//
// One inbox over four ALREADY-REAL backends — no new server work, pure aggregation:
//   • CivalaOS decisions awaiting workshop  GET /api/pdos/decisions        (runStatus)
//   • Verification flags (unresolved)   GET /api/verify/flags          → inline resolve
//   • Expert-panel countersigns         GET /api/expert-panel/consultations → inline countersign
//   • Drop-in review queue              GET /api/ingest/dropin/queue   → review in DropinPanel
//
// Each source maps to a unified ApprovalItem with either a deep-link to its home
// panel or an inline action. Reads go through the shared TanStack Query client
// (staleTime/SWR + 401-bounce); the two inline mutations invalidate their source.

import { unwrapOrThrow } from "@civala/shared-ui/query"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Set, type Wire } from "functype"

import { apiBase, apiClient } from "@/lib/client"

export type ApprovalSource = "decision" | "verify_flag" | "expert_countersign" | "dropin"

// Sources still backed by SEED fixtures (the kb.verification_flags /
// expert_panel_consultations tables are 100% seed today — nothing in prod writes
// them). decision (CivalaOS workshop) + dropin are real. See docs/GEN_data_provenance.md.
const DEMO_SOURCES = Set<ApprovalSource>(["verify_flag", "expert_countersign"])
export const isDemoSource = (source: ApprovalSource): boolean => DEMO_SOURCES.has(source)

// Link actions name a known route target (the panel resolves it to a literal
// TanStack route — `navigate` wants route literals, not dynamic strings).
export type ApprovalAction =
  | { readonly kind: "link"; readonly target: "decision"; readonly id: string }
  | { readonly kind: "link"; readonly target: "dropin" }
  | { readonly kind: "resolve_flag"; readonly flagId: string }
  | { readonly kind: "countersign"; readonly consultationId: string }

export type ApprovalSeverity = "high" | "medium" | "low"

export type ApprovalItem = Wire<{
  readonly id: string
  readonly source: ApprovalSource
  readonly title: string
  readonly subtitle: string
  readonly company: string | null
  readonly severity: ApprovalSeverity
  readonly createdAt: string | null
  readonly action: ApprovalAction
}>

type Rec = Record<string, unknown>
const asArr = (raw: unknown): ReadonlyArray<Rec> => (Array.isArray(raw) ? (raw as ReadonlyArray<Rec>) : [])
const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null)

// Flag severity text → our 3-level scale (critical collapses to high).
const flagSeverity = (s: unknown): ApprovalSeverity => {
  const v = typeof s === "string" ? s.toLowerCase() : ""
  if (v === "critical" || v === "high") return "high"
  if (v === "low" || v === "info") return "low"
  return "medium"
}

// ── Source → unified item mappers ──

const decisionItems = (rows: ReadonlyArray<Rec>): ApprovalItem[] =>
  rows
    .filter((d) => d.runStatus === "awaiting_workshop")
    .map((d) => {
      const id = String(d.decisionId ?? "")
      return {
        id: `decision:${id}`,
        source: "decision" as const,
        title: str(d.title) ?? str(d.program) ?? id,
        subtitle: `${str(d.program) ?? "—"} · ${str(d.decisionTypeId) ?? "decision"}`,
        company: str(d.program),
        severity: "high" as const,
        createdAt: str(d.createdAt),
        action: { kind: "link" as const, target: "decision" as const, id },
      }
    })

const flagItems = (rows: ReadonlyArray<Rec>): ApprovalItem[] =>
  rows
    .filter((f) => !f.resolution)
    .map((f) => ({
      id: `flag:${String(f.id ?? "")}`,
      source: "verify_flag" as const,
      title: str(f.description) ?? "Verification flag",
      subtitle: `${str(f.flagType) ?? "flag"} · ${str(f.companyId) ?? "—"}`,
      company: str(f.companyId),
      severity: flagSeverity(f.severity),
      createdAt: str(f.created),
      action: { kind: "resolve_flag" as const, flagId: String(f.id ?? "") },
    }))

const expertItems = (rows: ReadonlyArray<Rec>): ApprovalItem[] =>
  rows
    .filter((x) => x.countersignRequired === true && x.countersigned !== true)
    .map((x) => ({
      id: `expert:${String(x.id ?? "")}`,
      source: "expert_countersign" as const,
      title: str(x.question) ?? "Expert panel consultation",
      subtitle: `Tier ${String(x.tier ?? "?")} · ${str(x.companyId) ?? "—"}`,
      company: str(x.companyId),
      severity: "high" as const,
      createdAt: str(x.created),
      action: { kind: "countersign" as const, consultationId: String(x.id ?? "") },
    }))

const dropinItems = (rows: ReadonlyArray<Rec>): ApprovalItem[] =>
  rows.map((r) => ({
    id: `dropin:${String(r.docId ?? "")}`,
    source: "dropin" as const,
    title: str(r.title) ?? String(r.docId ?? "document"),
    subtitle: `${str(r.companyId) ?? "—"} · pending review`,
    company: str(r.companyId),
    severity: "medium" as const,
    createdAt: str(r.created),
    action: { kind: "link" as const, target: "dropin" as const },
  }))

const SEVERITY_RANK: Record<ApprovalSeverity, number> = { high: 0, medium: 1, low: 2 }

export const useApprovals = () => {
  const qc = useQueryClient()

  // Four independent reads — inlined (not a loop/helper) so the hook calls stay in
  // fixed order per the rules of hooks. TanStack dedups by queryKey, so calling this
  // hook in both the panel and the topbar bell shares one fetch per source.
  const decisions = useQuery({
    queryKey: ["approvals", "decisions"],
    queryFn: () => apiClient.getEither("/api/pdos/decisions", { decode: asArr }).then(unwrapOrThrow),
  })
  const flags = useQuery({
    queryKey: ["approvals", "flags"],
    queryFn: () => apiClient.getEither("/api/verify/flags", { decode: asArr }).then(unwrapOrThrow),
  })
  const expert = useQuery({
    queryKey: ["approvals", "expert"],
    queryFn: () => apiClient.getEither("/api/expert-panel/consultations", { decode: asArr }).then(unwrapOrThrow),
  })
  const dropin = useQuery({
    queryKey: ["approvals", "dropin"],
    queryFn: () => apiClient.getEither("/api/ingest/dropin/queue", { decode: asArr }).then(unwrapOrThrow),
  })

  const items: ApprovalItem[] = [
    ...decisionItems(decisions.data ?? []),
    ...flagItems(flags.data ?? []),
    ...expertItems(expert.data ?? []),
    ...dropinItems(dropin.data ?? []),
  ].sort((a, b) => {
    const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    if (sev !== 0) return sev
    // Oldest first within a severity (longest-waiting bubbles up).
    return (a.createdAt ?? "").localeCompare(b.createdAt ?? "")
  })

  const bySource: Record<ApprovalSource, number> = {
    decision: 0,
    verify_flag: 0,
    expert_countersign: 0,
    dropin: 0,
  }
  items.forEach((i) => {
    bySource[i.source] += 1
  })

  const loading = decisions.isPending || flags.isPending || expert.isPending || dropin.isPending

  // Inline mutations (the two sources without a dedicated review panel of their own).
  // The actor is NOT sent from the client — the server derives it from the verified
  // Principal and records the internal identity.users id (civala-ai/civala.ai#180).
  const resolveFlag = async (flagId: string, resolution: string): Promise<boolean> => {
    try {
      const res = await fetch(`${apiBase}/api/verify/flags/${flagId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resolution }),
      })
      if (res.ok) await qc.invalidateQueries({ queryKey: ["approvals", "flags"] })
      return res.ok
    } catch {
      return false
    }
  }

  const countersign = async (consultationId: string): Promise<boolean> => {
    try {
      const res = await fetch(`${apiBase}/api/expert-panel/consultations/${consultationId}/countersign`, {
        method: "POST",
      })
      if (res.ok) await qc.invalidateQueries({ queryKey: ["approvals", "expert"] })
      return res.ok
    } catch {
      return false
    }
  }

  const refresh = () => void qc.invalidateQueries({ queryKey: ["approvals"] })

  return { items, count: items.length, bySource, loading, resolveFlag, countersign, refresh }
}
